import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { apiRequest, ApiError } from '../lib/api';
import '../styles/agent.css';

/**
 * Ask NyumbaSuite AI.
 *
 * One component for all three portals. What differs between them is decided by
 * the SERVER — which skills exist for this caller, what the assistant is
 * allowed to look at, what it may prepare — so the panel itself does not need
 * to know whether it is sitting in a landlord's dashboard or the Control
 * Center. It asks, it renders, and it never decides anything about access.
 *
 * Two deliberate choices in how it presents answers.
 *
 * The EVIDENCE is shown, not just the prose. Every answer that came from data
 * arrives with the rows it was built from and a link to the screen that owns
 * them, so a figure can be checked in one click rather than believed. An
 * assistant that only produces sentences is one you have to trust; one that
 * shows its working is one you can verify.
 *
 * And when no model is configured the panel says so, quietly, instead of
 * pretending. The answers are still real — same queries, same permissions —
 * just plainer.
 */

export interface AgentReply {
  conversationId: string | null;
  answer: string;
  skill: string | null;
  level: 'information' | 'recommendation' | 'action' | null;
  rows?: Record<string, unknown>[];
  columns?: string[];
  route?: string;
  action?: { id: string; summary: string; expiresAt: string };
  /** Go to `route` now: the person asked to be taken there. */
  open?: boolean;
  /** Several records matched a command to open one. */
  links?: { label: string; route: string; hint?: string }[];
  suggestions: string[];
  /** The next questions in this part of the catalogue (§54). */
  followUps?: string[];
  degraded: boolean;
}

interface Turn {
  role: 'you' | 'agent';
  text: string;
  reply?: AgentReply;
}

type Mode = 'landlord' | 'platform' | 'public' | 'tenant' | 'owner';

/** A group of questions the picker offers, from GET /api/v1/agent. */
interface QuestionGroup { group: string; questions: string[] }

const LABEL: Record<Mode, string> = {
  landlord: 'Ask NyumbaSuite AI',
  platform: 'Ask NyumbaSuite AI',
  public: 'Ask NyumbaSuite AI',
  tenant: 'Ask NyumbaSuite AI',
  // The owner's assistant is an analyst, not a help desk, and the name is the
  // first thing that says so.
  owner: 'Portfolio advisor',
};

const OPENER: Record<Mode, string> = {
  landlord: 'Ask about your rent, tenants, vacancies or reports. I read only your own records.',
  platform: 'Ask about the platform — accounts, plans, growth. I cannot see any landlord’s tenants or payments.',
  public: 'Ask me anything about NyumbaSuite, or tell me what you are looking for.',
  tenant: 'Ask about your rent, your lease, your documents or a repair you have reported. '
    + 'I read only your own tenancy.',
  owner: 'Ask how your properties are performing — income, collection, arrears, expenses, '
    + 'occupancy or your P&L. Pick a question below, or write your own. '
    + 'I read only the properties you own.',
};

function cell(v: unknown): string {
  if (v === null || v === undefined) return '—';
  if (typeof v === 'number') return v.toLocaleString('en-KE');
  const s = String(v);
  // Dates arrive as ISO strings; nobody reads the time off a table.
  return /^\d{4}-\d{2}-\d{2}T/.test(s) ? s.slice(0, 10) : s;
}

const humanise = (s: string) => s.replace(/_/g, ' ').replace(/^\w/, c => c.toUpperCase());

/**
 * What to call the place an answer points at.
 *
 * The skills return a path, because a path is what a link needs. Printing the
 * path at the reader ("Open /admin/reports/plan-fit →") is showing them the
 * plumbing; they want the name of the screen. Anything unmapped falls back to
 * a generic label rather than leaking the URL into the sentence.
 */
const DESTINATION: Record<string, string> = {
  '/': 'your dashboard',
  '/arrears': 'Arrears',
  '/rent': 'Rent & payments',
  '/vacancies': 'Vacancies',
  '/tenancies': 'Tenancies',
  '/reports': 'Reports',
  '/homes': 'Find your next home',
  '/blog': 'the blog',
  '/register': 'sign-up',
  '/demo': 'Request a demo',
  '/solutions': 'Solutions',
  '/#pricing': 'pricing',
  '/admin/reports/census': 'the platform census',
  '/admin/reports/register': 'the property register',
  '/admin/reports/growth': 'the growth report',
  '/admin/reports/ranking': 'the landlord ranking',
  '/admin/reports/plan-fit': 'the plan-fit report',
  '/invoices': 'Invoices',
  '/credit-notes': 'Credit notes',
  '/invoices/new': 'a new invoice',
  '/payments': 'Payments',
  '/payments/new': 'the payment form',
  '/tenants': 'Tenants',
  '/tenants/new': 'a new tenant',
  '/tenancies/new': 'a new tenancy',
  '/leases': 'Leases',
  '/leases/new': 'a new lease',
  '/properties': 'Properties',
  '/properties/new': 'a new property',
  '/units': 'Units',
  '/maintenance': 'Maintenance',
  '/settings': 'Settings',
  '/portal/rent': 'Rent',
  '/portal/statement': 'your statement',
  '/portal/invoices': 'your invoices',
  '/portal/payments': 'your payments',
  '/portal/maintenance': 'Repairs',
  '/portal/tenancy': 'your tenancy',
  '/portal/documents': 'your documents',
  '/owner/financials': 'Financials',
  '/owner/reports': 'Reports',
  '/owner/properties': 'your properties',
};

/* A single record's page, named by what it is rather than by its id. */
const RECORD: [RegExp, string][] = [
  [/^\/tenants\/[^/]+$/, 'this tenant'],
  [/^\/invoices\/[^/]+$/, 'this invoice'],
  [/^\/leases\/[^/]+$/, 'this lease'],
  [/^\/properties\/[^/]+$/, 'this property'],
  [/^\/owner\/properties\/[^/]+$/, 'this property'],
  [/^\/receipts\/[^/]+$/, 'this receipt'],
];

const destinationName = (route: string) => {
  const path = route.split('?')[0]!;
  if (DESTINATION[route]) return DESTINATION[route];
  // Narrowed to the records an answer named: say how many, so the reader
  // knows the list will hold exactly those.
  const focused = route.match(/[?&](?:ids|tenantIds)=([^&]+)/);
  if (focused) {
    const n = decodeURIComponent(focused[1]!).split(',').length;
    return `the ${n} in ${DESTINATION[path] ?? 'the list'}`;
  }
  const within = route.match(/[?&]expiringWithinDays=(\d+)/);
  if (within) return `${DESTINATION[path] ?? 'the list'} ending within ${within[1]} days`;
  if (route.includes('?status=')) {
    const status = route.split('?status=')[1]!.replace(/_/g, ' ');
    return `${status} ${(DESTINATION[path] ?? 'records').toLowerCase()}`;
  }
  if (route.includes('?search=')) return `${DESTINATION[path] ?? 'the list'}, searched`;
  return DESTINATION[path] ?? RECORD.find(([re]) => re.test(path))?.[1] ?? 'the full screen';
};

/*
 * Commands the assistant carries out rather than answers. Shown once, in the
 * opener, because nobody guesses that a chat box will also take them to a
 * page, and every one of these works with no AI key configured.
 */
const COMMANDS: Partial<Record<Mode, string[]>> = {
  landlord: ['Open draft invoices', 'Open tenancy for Wanjiru', 'New invoice', 'Record a payment'],
  tenant: ['Open my statement', 'Report a repair', 'Open my invoices'],
  owner: ['Open financials', 'Open reports'],
};

/** Ctrl+K on Windows and Linux, Cmd+K on a Mac. */
const SHORTCUT = typeof navigator !== 'undefined' && /mac|iphone|ipad/i.test(navigator.platform ?? '')
  ? '⌘K' : 'Ctrl+K';

/**
 * The last few questions this person asked, kept in their own browser (§52).
 *
 * Per PORTAL, because a landlord who also owns property asks different things
 * in each and a merged list would be neither. Five, because the value is
 * "ask that again", not a history — a longer list is a transcript nobody
 * reads.
 *
 * Deliberately localStorage rather than the server. The conversation is
 * already stored server-side and readable in the Control Centre; this is a
 * convenience for one browser, and putting it on the account would mean a
 * shared office machine offering one person's questions to the next.
 *
 * Every access is wrapped: a private window, cleared site data or a browser
 * set to block storage all throw here, and none of them is a reason for the
 * assistant to fail to open.
 */
const RECENT_MAX = 5;
const recentKey = (mode: Mode) => `ns.agent.recent.${mode}`;

function readRecent(mode: Mode): string[] {
  try {
    const raw = window.localStorage.getItem(recentKey(mode));
    const list = raw ? JSON.parse(raw) : [];
    return Array.isArray(list) ? list.filter(q => typeof q === 'string').slice(0, RECENT_MAX) : [];
  } catch { return []; }
}

function rememberRecent(mode: Mode, q: string): string[] {
  const trimmed = q.trim();
  if (!trimmed) return readRecent(mode);
  const next = [trimmed, ...readRecent(mode).filter(x => x.toLowerCase() !== trimmed.toLowerCase())]
    .slice(0, RECENT_MAX);
  try { window.localStorage.setItem(recentKey(mode), JSON.stringify(next)); } catch { /* not fatal */ }
  return next;
}

export function AgentPanel({ mode }: { mode: Mode }) {
  const [recent, setRecent] = useState<string[]>(() => readRecent(mode));
  const [open, setOpen] = useState(false);
  const [turns, setTurns] = useState<Turn[]>([]);
  const [question, setQuestion] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [groups, setGroups] = useState<QuestionGroup[]>([]);
  const [configured, setConfigured] = useState<boolean | null>(null);
  const conversation = useRef<string | null>(null);
  const sessionId = useRef<string>(crypto.randomUUID());
  const scroller = useRef<HTMLDivElement>(null);
  const input = useRef<HTMLInputElement>(null);
  const go = useNavigate();

  /*
   * Ctrl+K (Cmd+K) opens the assistant from anywhere, the way a command bar
   * does in most tools people already use. Ignored while typing in another
   * field only when a modifier is not held, which Ctrl+K always is, so it
   * works from inside a form too: that is where "take me to X" is wanted.
   */
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && !e.shiftKey && !e.altKey && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setOpen(o => !o);
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  // What this caller may ask, answered by the server. The chips are not
  // decoration: with no model configured they are the reliable path to an
  // answer, so they are fetched even before the first question.
  useEffect(() => {
    if (!open || suggestions.length > 0) return;
    const path = mode === 'public' ? '/api/v1/agent/public' : '/api/v1/agent';
    apiRequest<{
      suggestions: string[]; status?: { configured: boolean }; questionGroups?: QuestionGroup[];
    }>(path)
      .then(r => {
        setSuggestions(r.suggestions ?? []);
        setGroups(r.questionGroups ?? []);
        setConfigured(r.status?.configured ?? null);
      })
      .catch(() => { /* the panel still works; chips are a convenience */ });
  }, [open, mode, suggestions.length]);

  useEffect(() => {
    if (open) input.current?.focus();
  }, [open]);

  useEffect(() => {
    scroller.current?.scrollTo({ top: scroller.current.scrollHeight, behavior: 'smooth' });
  }, [turns, busy]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);

  const send = async (text: string) => {
    setRecent(rememberRecent(mode, text));
    const q = text.trim();
    if (!q || busy) return;
    setQuestion('');
    setError(null);
    setTurns(t => [...t, { role: 'you', text: q }]);
    setBusy(true);
    try {
      const reply = mode === 'public'
        ? await apiRequest<AgentReply>('/api/v1/agent/public/ask', {
            method: 'POST', body: { question: q, sessionId: sessionId.current },
          })
        : await apiRequest<AgentReply>('/api/v1/agent/ask', {
            method: 'POST',
            body: { question: q, ...(conversation.current ? { conversationId: conversation.current } : {}) },
          });
      conversation.current = reply.conversationId ?? conversation.current;
      if (reply.suggestions?.length) setSuggestions(reply.suggestions);
      setTurns(t => [...t, { role: 'agent', text: reply.answer, reply }]);
      // Asked to be taken somewhere, and there is exactly one place: go. The
      // panel closes so the page is what they see; the turn stays in the
      // conversation for when it is opened again.
      if (reply.open && reply.route) {
        go(reply.route);
        setOpen(false);
      }
    } catch (e) {
      const msg = e instanceof ApiError
        ? (e.status === 429 ? 'That is a lot of questions at once. Give it a moment.' : e.message)
        : 'The assistant could not be reached.';
      setError(msg);
      setTurns(t => [...t, { role: 'agent', text: msg }]);
    } finally {
      setBusy(false);
    }
  };

  const decide = async (actionId: string, decision: 'confirm' | 'decline') => {
    setBusy(true);
    try {
      const r = await apiRequest<{ status: string }>(`/api/v1/agent/actions/${actionId}/decide`, {
        method: 'POST', body: { decision },
      });
      setTurns(t => [...t, {
        role: 'agent',
        text: r.status === 'executed' ? 'Done.' : 'Left alone — nothing was changed.',
      }]);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'That action could not be completed.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <button
        type="button"
        className={`agent-launcher${open ? ' open' : ''}`}
        aria-expanded={open}
        aria-controls="agent-panel"
        onClick={() => setOpen(o => !o)}
      >
        <span className="agent-spark" aria-hidden>✦</span>
        <span className="agent-launcher-text">{LABEL[mode]}</span>
        {mode !== 'public' && <kbd className="agent-kbd" aria-hidden>{SHORTCUT}</kbd>}
      </button>

      {open && (
        <>
          <div className="agent-scrim" onClick={() => setOpen(false)} aria-hidden />
          <aside id="agent-panel" className="agent-panel" role="dialog" aria-label="NyumbaSuite assistant">
            <header className="agent-head">
              <span className="agent-spark" aria-hidden>✦</span>
              <div className="agent-head-text">
                <strong>NyumbaSuite AI</strong>
                <span className="agent-scope">
                  {mode === 'landlord' && 'Your portfolio only'}
                  {mode === 'platform' && 'Platform data only'}
                  {mode === 'public' && 'Public information'}
                  {mode === 'tenant' && 'Your tenancy only'}
                </span>
              </div>
              <button type="button" className="agent-close" onClick={() => setOpen(false)} aria-label="Close">×</button>
            </header>

            <div className="agent-body" ref={scroller}>
              {turns.length === 0 && (
                <div className="agent-opener">
                  <p>{OPENER[mode]}</p>
                  {(COMMANDS[mode]?.length ?? 0) > 0 && (
                    <div className="agent-commands">
                      <span className="agent-followups-label">
                        I can also take you straight to a page. Try
                      </span>
                      <div className="agent-chips">
                        {COMMANDS[mode]!.map(c => (
                          <button key={c} type="button" className="agent-chip agent-chip-go"
                            disabled={busy} onClick={() => void send(c)}>{c}</button>
                        ))}
                      </div>
                    </div>
                  )}
                  {configured === false && (
                    <p className="agent-note">
                      No language model is configured, so I answer by matching your question to what
                      I can look up. The figures are real; the wording is plain.
                    </p>
                  )}
                </div>
              )}

              {turns.map((t, i) => (
                <div key={i} className={`agent-turn ${t.role}`}>
                  <div className="agent-bubble">{t.text}</div>

                  {t.reply?.rows && t.reply.rows.length > 0 && (
                    <div className="agent-evidence">
                      <div className="agent-table-wrap">
                        <table className="agent-table">
                          <thead>
                            <tr>{(t.reply.columns ?? []).map(c => <th key={c}>{humanise(c)}</th>)}</tr>
                          </thead>
                          <tbody>
                            {t.reply.rows.slice(0, 8).map((row, ri) => (
                              <tr key={ri}>
                                {(t.reply!.columns ?? []).map(c => <td key={c}>{cell(row[c])}</td>)}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                      {t.reply.rows.length > 8 && (
                        <p className="agent-more">Showing 8 of {t.reply.rows.length}.</p>
                      )}
                    </div>
                  )}

                  {t.reply?.action && (
                    <div className="agent-action">
                      <p>{t.reply.action.summary}</p>
                      <div className="agent-action-row">
                        <button type="button" className="btn btn-primary btn-sm"
                          onClick={() => decide(t.reply!.action!.id, 'confirm')} disabled={busy}>
                          Confirm
                        </button>
                        <button type="button" className="btn btn-secondary btn-sm"
                          onClick={() => decide(t.reply!.action!.id, 'decline')} disabled={busy}>
                          Not now
                        </button>
                      </div>
                    </div>
                  )}

                  {t.reply?.links && t.reply.links.length > 0 && (
                    <ul className="agent-links">
                      {t.reply.links.map(l => (
                        <li key={l.route}>
                          <Link to={l.route} onClick={() => setOpen(false)}>
                            <span className="agent-link-label">{l.label}</span>
                            {l.hint && <span className="agent-link-hint">{l.hint}</span>}
                          </Link>
                        </li>
                      ))}
                    </ul>
                  )}

                  {t.reply?.route && (
                    <Link className="agent-route" to={t.reply.route} onClick={() => setOpen(false)}>
                      {t.reply.open ? 'Opened' : 'Open'} {destinationName(t.reply.route)} →
                    </Link>
                  )}

                  {/* "You may also want to ask" (§54). Only on the LAST turn:
                      offering next steps under an answer three questions back
                      is clutter, and the reader has already moved on from it. */}
                  {i === turns.length - 1 && (t.reply?.followUps?.length ?? 0) > 0 && (
                    <div className="agent-followups">
                      <span className="agent-followups-label">You may also want to ask</span>
                      <div className="agent-chips">
                        {t.reply!.followUps!.map(f => (
                          <button key={f} type="button" className="agent-chip"
                            onClick={() => send(f)} disabled={busy}>{f}</button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))}

              {busy && <div className="agent-turn agent"><div className="agent-bubble agent-thinking">
                <span /><span /><span />
              </div></div>}
            </div>

            {/*
              * Where there is a whole catalogue, a picker; otherwise the chips.
              *
              * Four chips are enough to start a conversation and useless for
              * finding out what can be asked. The owner's advisor answers
              * forty-odd specified questions across nine themes, and a row of
              * chips can only ever show a tenth of them — so an owner gets a
              * grouped list to choose from, which is also the answer to "what
              * can I even ask this thing". Choosing one sends it immediately;
              * there is no second step, because the question IS the action.
              */}
            {/* Asked before, in this browser (§52). Only before the first
                question of a session: once a conversation is running, the
                thing above the box is the conversation. */}
            {turns.length === 0 && recent.length > 0 && (
              <div className="agent-recent">
                <span className="agent-recent-label">Asked recently</span>
                <div className="agent-chips">
                  {recent.map(r => (
                    <button key={r} type="button" className="agent-chip" disabled={busy}
                      onClick={() => void send(r)}>{r}</button>
                  ))}
                </div>
              </div>
            )}

            {groups.length > 0 ? (
              <div className="agent-picker">
                <label className="agent-picker-label" htmlFor="agent-question-pick">
                  Choose a question
                </label>
                <select
                  id="agent-question-pick"
                  className="agent-picker-select"
                  disabled={busy}
                  value=""
                  onChange={(e) => {
                    const q = e.target.value;
                    // Reset first, so picking the same question twice re-asks
                    // it rather than doing nothing the second time.
                    e.target.value = '';
                    if (q) void send(q);
                  }}
                >
                  <option value="">Browse all questions…</option>
                  {groups.map(g => (
                    <optgroup key={g.group} label={g.group}>
                      {g.questions.map(q => <option key={q} value={q}>{q}</option>)}
                    </optgroup>
                  ))}
                </select>
              </div>
            ) : suggestions.length > 0 && (
              <div className="agent-chips">
                {suggestions.map(s => (
                  <button key={s} type="button" className="agent-chip" disabled={busy}
                    onClick={() => void send(s)}>{s}</button>
                ))}
              </div>
            )}

            <form className="agent-compose" onSubmit={e => { e.preventDefault(); void send(question); }}>
              <input
                ref={input}
                className="agent-input"
                value={question}
                onChange={e => setQuestion(e.target.value)}
                placeholder={mode === 'public' ? 'Ask a question…' : 'Ask a question, or say "open…"'}
                maxLength={500}
                aria-label="Ask the assistant"
              />
              <button type="submit" className="agent-send" disabled={busy || question.trim().length < 2}
                aria-label="Send">→</button>
            </form>

            {error && <p className="agent-error" role="alert">{error}</p>}

            <p className="agent-foot">
              {mode === 'public'
                ? 'I have no access to any account, landlord or tenant information.'
                : mode === 'tenant'
                ? 'Answers are read live from your own tenancy. I cannot see your neighbours, '
                  + 'other units, or anything else in your landlord\u2019s records.'
                : 'Answers are read live from your own records, under your own permissions. '
                  + 'Anything that would change data waits for you to confirm it.'}
            </p>
          </aside>
        </>
      )}
    </>
  );
}
